/**
 * Sprint 24 — ConnectionRow.
 *
 * One row in the unified `/connections` list. Renders avatar + name + type chip
 * + meta line + status pill + count + chevron. First-party rows get a 2px purple
 * left-border accent and a star avatar (Q10). Used only by ConnectionsDashboardPage.
 */

import type { CSSProperties, KeyboardEvent, MouseEvent } from "react";
import {
  type UnifiedConnection,
  chipTypeFor,
  isFirstParty,
  useSetConnectionEnabled,
} from "../../../hooks/use-connections";
import { ConnectionTypeChip, CONNECTION_CHIP_COLORS } from "./ConnectionTypeChip";
import { ConnectionStatusPill } from "./ConnectionStatusPill";

const FIRST_PARTY_ACCENT = "#534AB7";

function avatarIcon(c: UnifiedConnection): string {
  if (isFirstParty(c)) return "ti-star";
  return c.kind === "mcp" ? "ti-plug" : "ti-file-code";
}

function metaLine(c: UnifiedConnection): string {
  if (c.kind === "mcp") {
    const auth =
      c.auth_type === "none"
        ? "No auth"
        : c.auth_type === "oauth2"
          ? "OAuth"
          : c.auth_type === "bearer"
            ? "Bearer"
            : "Header";
    return `${c.url} · ${auth}`;
  }
  return `${c.base_url || "OpenAPI"} · ${c.auth_kind.replace(/_/g, " ")}`;
}

function countLabel(c: UnifiedConnection): string {
  return c.kind === "mcp" ? `${c.count} tools` : `${c.count} ops`;
}

export interface ConnectionRowProps {
  connection: UnifiedConnection;
  onClick: () => void;
  /** Sprint 24.5 — first-party template accent colour for the left-border. */
  accentColor?: string;
}

export function ConnectionRow({ connection, onClick, accentColor }: ConnectionRowProps) {
  const firstParty = isFirstParty(connection);
  const chip = chipTypeFor(connection);
  const ramp = CONNECTION_CHIP_COLORS[chip];
  const isError = connection.status === "error";
  const isEnabled = connection.enabled !== false;
  const setEnabled = useSetConnectionEnabled();

  function toggleEnabled(e: MouseEvent) {
    e.stopPropagation(); // don't navigate into the detail page
    setEnabled.mutate({
      connection_kind: connection.kind,
      connection_id: connection.connection_id,
      enabled: !isEnabled,
    });
  }

  function onRowKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  }

  const row: CSSProperties = {
    background: "var(--color-background-secondary)",
    borderRadius: "var(--border-radius-md)",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    border: "0.5px solid var(--color-border-secondary)",
    width: "100%",
    textAlign: "left",
    fontFamily: "var(--font-sans)",
    // Muted OR errored connections read dimmer.
    opacity: isError || !isEnabled ? 0.6 : 1,
    ...(firstParty ? { borderLeft: `2px solid ${accentColor ?? FIRST_PARTY_ACCENT}` } : {}),
  };

  const avatar: CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: "var(--border-radius-md)",
    background: ramp.bg,
    color: ramp.fg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flexShrink: 0,
  };

  return (
    <div role="button" tabIndex={0} style={row} onClick={onClick} onKeyDown={onRowKeyDown}>
      <span style={avatar}>
        <i className={`ti ${avatarIcon(connection)}`} />
      </span>

      <span style={body}>
        <span style={titleRow}>
          <span style={name} title={connection.name}>
            {connection.name}
          </span>
          <ConnectionTypeChip type={chip} />
        </span>
        <span style={meta} title={metaLine(connection)}>
          {metaLine(connection)}
        </span>
      </span>

      <span style={trailing}>
        <ConnectionStatusPill status={connection.status} />
        <span style={count}>{countLabel(connection)}</span>
        {/* Spec 10.1B follow-on — per-connection mute toggle. */}
        <button
          type="button"
          onClick={toggleEnabled}
          disabled={setEnabled.isPending}
          title={isEnabled ? "Enabled — click to mute (hide its tools)" : "Muted — click to enable"}
          aria-label={isEnabled ? "Mute connection" : "Enable connection"}
          aria-pressed={isEnabled}
          style={toggleBtn}
        >
          <i
            className={`ti ${isEnabled ? "ti-toggle-right" : "ti-toggle-left"}`}
            style={{ fontSize: 18, color: isEnabled ? "var(--color-text-success)" : "var(--color-text-tertiary)" }}
          />
        </button>
        <i
          className="ti ti-chevron-right"
          style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}
        />
      </span>
    </div>
  );
}

const toggleBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  padding: 2,
  cursor: "pointer",
  lineHeight: 0,
};

const body: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
};

const titleRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
};

const name: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const meta: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-secondary)",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const trailing: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexShrink: 0,
};

const count: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};
